import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  allocateSchedule,
  getResourceAllocations,
  approveTimesheet,
  editTimesheet as editTimesheetAPI,
  applyLeave as applyLeaveAPI,
  deleteResource, modifyAllocation,
  bulkUpdateTimesheet,
  deleteResources,
  recalculateTimesheets
} from "../../actions/timesheet.action";
import { getUser } from "../../actions/user-management.action";
import moment from "moment";
import { formatDecimalTime } from "../../util/common";
import idx from "idx";
import AllocateResource from "./allocate-resource-dialog";
import ConfirmAllocateResource from "./confirm-allocate-resource-dialog";
import DeleteDialog from "./../../components/delete-dialog";
import TimesheetActionConfirmation from "./../../components/timesheet-action-confirmation-dialog";
import ReviewCommentModal from "./../../components/review-comment-modal";
import { getProjectList } from "../../actions/project-management.action";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Grid, Menu, MenuItem } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CustomTable from "../../components/table/table";
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { errorSnackBar, successSnackBar, warningSnackBar } from "../../actions/common.action";
import Form from 'react-bootstrap/Form'
import BulkAllocationModal from '../../components/bulk-allocation-dialog'

const ViewTimeSheet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const { permissions, id: managerId } = useSelector((state) => state.user.userData);

  const [allocations, setAllocations] = useState([]);
  const [columns, setColumns] = useState([]);
  const [showAllocateResource, setShowAllocateResource] = useState(false);
  const [timesheetStatus, setTimesheetStatus] = useState("");
  const [confirmDialogState, setConfirmDialogState] = useState(false);
  const [confirmData, setConfirmData] = useState([]);
  const [deleteDialogState, setDeleteDialogState] = useState(false);
  const [actionConfirmationState, setActionConfirmationState] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [allocationId, setAllocationId] = useState(null);
  const [allocationResourceId, setAllocationResourceId] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [bulkAllocationModal, setBulkAllocationModal] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [useFilters, setUseFilters] = useState(true);
  const [filterParams, setFilterParams] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [reviewCommentModalOpen, setReviewCommentModalOpen] = useState(false);
  const [pendingCommentAction, setPendingCommentAction] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [isBulkContext, setIsBulkContext] = useState(false);
  const [recalculateConfirmationOpen, setRecalculateConfirmationOpen] = useState(false);
  const [recalculateIds, setRecalculateIds] = useState([]);
  const [isRecalculating, setIsRecalculating] = useState(false);

  useEffect(() => {
    getColumns();

    // Parse filter parameters from URL on initial load
    const projectType = searchParams.get('projectType');
    const projectId = searchParams.get('projectId');
    const startTime = searchParams.get('startTime');
    const endTime = searchParams.get('endTime');

    // Check if we have filter parameters
    const hasFilters = projectType || projectId || startTime || endTime;

    if (hasFilters) {
      // Store filter parameters
      const params = {
        projectType,
        projectId,
        startTime: startTime ? decodeURIComponent(startTime) : null,
        endTime: endTime ? decodeURIComponent(endTime) : null
      };
      setFilterParams(params);
      setUseFilters(true); // Ensure filters are applied on initial load
    } else {
      // No filters, show all data
      setUseFilters(false);
    }
  }, []);

  useEffect(() => {
    getAllocations();
  }, [useFilters, filterParams]);

  useEffect(() => {
    bulkUpdate();
  }, [selectAll]);

  const getColumns = () => {
    const columns = [
      { title: "Emp Name", field: "empName" },
      { title: "Project Name", field: "projectName" },
      {
        title: "Section\\Task",
        field: "f_taskName",
        class: "text-uppercase"
      },
      {
        title: "Status",
        field: "status",
        render: (rowData) => (
          <div className="d-flex align-items-center gap-2">
            <span>{rowData.status}</span>
            {rowData.comment && (
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="auto"
                overlay={
                  <Popover id={`popover-comment-${rowData.id}`} style={{ maxWidth: '400px' }}>
                    <Popover.Header as="h3">Review Comment</Popover.Header>
                    <Popover.Body style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '13px',
                      lineHeight: '1.4'
                    }}>
                      {rowData.comment}
                    </Popover.Body>
                  </Popover>
                }
              >
                <ChatBubbleOutlineIcon
                  fontSize="small"
                  style={{ cursor: "pointer", color: "#1976d2" }}
                />
              </OverlayTrigger>
            )}
          </div>
        ),
      },
      {
        title: "Start & End Time",
        field: "allocationTime",
        defaultSort: "desc",
      },
      {
        title: "Breakdown",
        field: "work_hour_breakdown",
        render: (rowData) => {
          const b = rowData.work_hour_breakdown;
          if (!b?.time_periods?.length) return '—';

          const periodLabels = b.time_periods.map(p => {
            let label = 'OT'
            if (p.time_type === 'STANDARD') label = 'Std'
            else if (p.time_type === 'UNPAID_ALLOWANCE') label = 'Unpaid'
            return `${formatDecimalTime(p.hours)} ${label}`
          });
          const classifiedHours = b.time_periods.reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
          const unclassifiedHours = (rowData.entryHours || 0) - classifiedHours;
          const hasUnclassified = unclassifiedHours > 0.05;

          if (!hasUnclassified) return periodLabels.join(' | ');

          return (
            <OverlayTrigger
              placement="top"
              overlay={
                <Popover>
                  <Popover.Body style={{ fontSize: '0.8rem' }}>
                    Some worked hours fall outside the configured work hour periods and are not included in the cost breakdown. Review the Work Hour configuration to ensure full-day coverage.
                  </Popover.Body>
                </Popover>
              }
            >
              <span>
                {periodLabels.join(' | ')}
                {' | '}
                <span style={{ color: '#f59e0b', cursor: 'help' }}>
                  ⚠ {formatDecimalTime(unclassifiedHours)} Unclassified
                </span>
              </span>
            </OverlayTrigger>
          );
        }
      },
      {
        title: "", field: "selectall", render: (rowData) => {
          if (rowData.isHeader) {
            return <div className="table-checkbox table-checkbox-ch text-left">
              <Form.Check
                type={'checkbox'}
                checked={rowData.rows.filter(_ => _.status === 'PENDING').length ? rowData.rows.filter(_ => _.status === 'PENDING').every(_ => _.isSelected) : false}
                value={rowData.rows.filter(_ => _.status === 'PENDING').length ? rowData.rows.filter(_ => _.status === 'PENDING').every(_ => _.isSelected) : false}
                onChange={() => handleSelectAll()}
                disabled={rowData.rows.filter(_ => _.status === 'PENDING').length === 0}
              />
            </div>;
          }
          if (rowData.status !== 'PENDING' || !rowData.id) return;
          return (
            <div className="table-checkbox table-checkbox-ch text-left">
              <Form.Check
                id={rowData.id}
                type={'checkbox'}
                label={rowData.description}
                value={rowData.isSelected}
                checked={Boolean(rowData.isSelected)}
                onChange={(e) => handleBulkUpdate(rowData.id, e.target.checked)}
              />
            </div>
          );
        }
      },
    ];
    setColumns(columns);
  };

  const handleSelectAll = () => {
    setSelectAll(prev => !prev)
  }

  const bulkUpdate = () => {
    allocations.map((row) => {
      handleBulkUpdate(row.id, selectAll)
    });
  }

  const getAllocations = async () => {
    const writePermission = permissions.timesheetApprover === 2;

    if (!permissions.timesheetApprover) {
      dispatch(warningSnackBar(`Sorry, you don't have permissions to do this action`));
      return;
    }

    let projects = projectData;
    if (!projects) {
      projects = await dispatch(getProjectList(null, null, null, null, null, managerId));
      setProjectData(projects || []);
    }

    let projectIds = [managerId];
    Array.isArray(projects) &&
      projects.map((project) => projectIds.push(project.id));

    if (writePermission) {
      projectIds = null;
    }

    const params = {
      // userId,
      showRejected: true,
      projectIds,
    };

    if (useFilters && filterParams) {
      if (filterParams.projectType) params.projectType = filterParams.projectType;
      if (filterParams.projectId) params.projectId = filterParams.projectId;
      if (filterParams.startTime) params.startTime = filterParams.startTime;
      if (filterParams.endTime) params.endTime = filterParams.endTime;
    }

    const [list, user] = await Promise.all([
      dispatch(getResourceAllocations(params)),
      dispatch(getUser(userId)),
    ]);

    const empName =
      idx(
        user,
        (_) =>
          (user.firstName && user.firstName != null ? user.firstName : "") +
          " " +
          (user.lastName && user.lastName != null ? user.lastName : "")
      ) || "";
    const resourceAllocations = [];
    Array.isArray(list) &&
      list.map((allocation) => {
        let projectName;
        if (allocation.projectType === "SERVICE") {
          projectName = idx(allocation, (_) => _.service.name) || "";
        } else {
          projectName = idx(allocation, (_) => _.project.name) || "";
        }

        const date = moment.utc(allocation.startTime).format("DD-MM-YYYY");
        const start = moment.utc(allocation.startTime).format("HH:mm");
        const end = moment.utc(allocation.endTime).format("HH:mm");

        const allocationTime = `${date} ${start} to ${end}`;

        const entryMs = new Date(allocation.endTime) - new Date(allocation.startTime);
        resourceAllocations.push({
          id: allocation.id,
          allocationTime,
          projectName,
          taskName: allocation.taskName,
          empName,
          allocationResourceId: allocation.id,
          status: allocation.status,
          comment: allocation.comment,
          work_hour_breakdown: allocation.work_hour_breakdown,
          entryHours: entryMs > 0 ? entryMs / (1000 * 3600) : 0,
          f_taskName: allocation.f_taskName,
          isSelected: false,
        });
      });
    setAllocations(resourceAllocations);
  };

  const handleMenu = (rowData, event) => {
    setAnchorEl(event?.currentTarget);
    setAllocationId(rowData.id);
    setAllocationResourceId(rowData.allocationResourceId);
    setTimesheetStatus(rowData.status);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const modify = () => {
    setShowAllocateResource(true);
    setAnchorEl(null);
  };

  const updateTimesheetStatus = async (status) => {
    handleClose();
    setPendingAction(status);
    setIsBulkContext(false);
    setReviewCommentModalOpen(true);
  };

  const formatRecalculationSummary = (result) => {
    const data = result?.data || result || {};
    const recalculated = data.recalculated || [];
    const issues = [...(data.skipped || []), ...(data.failed || [])]
      .map((item) => `${item.id} — ${item.reason}`)
      .join('; ');

    return {
      hasIssues: issues.length > 0,
      message: issues.length > 0
        ? `Recalculated ${recalculated.length} timesheet record(s). ${issues}`
        : result?.message || `Recalculated ${recalculated.length} timesheet record(s).`,
    };
  };

  const requestRecalculation = (ids) => {
    const pendingIds = ids.filter((id) => allocations.some((allocation) => allocation.id === id && allocation.status === "PENDING"));
    if (!pendingIds.length) {
      dispatch(warningSnackBar("Select at least one pending timesheet to recalculate."));
      return;
    }
    handleClose();
    setRecalculateIds(pendingIds);
    setRecalculateConfirmationOpen(true);
  };

  const runRecalculation = async (ids) => {
    if (!ids.length || isRecalculating) return;
    setIsRecalculating(true);
    try {
      const result = await dispatch(recalculateTimesheets(ids));
      const summary = formatRecalculationSummary(result);
      const recalculatedIds = new Set(((result?.data || result || {}).recalculated || []).map((item) => item.id));
      setAllocations((current) => current.map((allocation) => (
        recalculatedIds.has(allocation.id) ? { ...allocation, isSelected: false } : allocation
      )));
      await getAllocations();
      dispatch(summary.hasIssues ? warningSnackBar(summary.message) : successSnackBar(summary.message));
      setRecalculateConfirmationOpen(false);
      setRecalculateIds([]);
      setBulkAllocationModal(false);
    } catch (error) {
      dispatch(errorSnackBar(error?.response?.data?.message || error?.message || "Failed to recalculate timesheets."));
    } finally {
      setIsRecalculating(false);
    }
  };

  const confirmRecalculation = async () => {
    await runRecalculation(recalculateIds);
  };

  const handleReviewCommentSubmit = (comment) => {
    setReviewComment(comment);
    setReviewCommentModalOpen(false);
    setActionConfirmationState(true);
  };

  const confirmStatusUpdate = async (status, comment) => {
    setActionConfirmationState(false);
    const data = {
      status,
      comment: comment || reviewComment || "",
      userName: allocations.find(a => a.id === allocationId)?.empName || ""
    };
    try {
      await dispatch(approveTimesheet(allocationId, data));
      getAllocations();
      setPendingAction(null);
      setReviewComment("");
    } catch (e) {
      console.error("Failed to approve timesheet", e);
    }
  };

  const allocateResource = async (data) => {
    let start = new Date(data.startTime);
    let end = new Date(data.endTime);
    let startTime = new Date(data.startDate);
    let endTime = new Date(data.endDate);
    startTime.setHours(start.getHours(), start.getMinutes(), 0, 0);
    endTime.setHours(end.getHours(), end.getMinutes(), 0, 0);
    startTime = moment.utc(startTime).format("YYYY-MM-DDTHH:mmZ").toString();
    endTime = moment.utc(endTime).format("YYYY-MM-DDTHH:mmZ").toString();
    data["startTime"] = startTime;
    data["endTime"] = endTime;
    delete data.startDate;
    delete data.endDate;
    if (allocationId) data.allocationType = "WORK";
    await handleAllocation(data, "WORK");
  };

  const editTimesheet = async (data) => {
    data.allocationResourceId = allocationResourceId;
    data.previousType = "WORK";
    data.startTime = moment.utc(data.startTime).format("YYYY-MM-DDTHH:mmZ").toString();
    data.endTime = moment.utc(data.endTime).format("YYYY-MM-DDTHH:mmZ").toString();
    return await dispatch(editTimesheetAPI(allocationId, data));
  };

  const cancelHandler = () => {
    navigate("/timesheet/allocations");
  };

  const allocateScheduleHandler = async (data) => {
    try {
      await dispatch(allocateSchedule(data));
      getAllocations();
      setConfirmDialogState(false);
    } catch (e) {
      console.error("Error in allocateScheduleHandler:", e);
      setConfirmDialogState(true);
    }
  };


  const applyLeave = async (data) => {
    if (allocationId) data.allocationType = "LEAVE";
    await handleAllocation(data, "LEAVE");
  };

  const handleAllocation = async (data, type) => {
    try {
      let allocationsList = [];
      if (allocationId) {
        allocationsList = await editTimesheet(data);
      } else {
        if (type === "LEAVE") {
          allocationsList = await dispatch(applyLeaveAPI(data));
        } else if (type === "WORK") {
          allocationsList = await dispatch(allocateResource(data));
        }
      }
      setConfirmDialogState(true);
      setConfirmData(allocationsList);
      setShowAllocateResource(false);
    } catch (e) {
      setShowAllocateResource(true);
    }
  };

  const deleteHandler = async () => {
    await dispatch(deleteResource("WORK", allocationId));
    handleClose();
    await getAllocations();
    setDeleteDialogState(false);
  };

  const deleteDialogHandler = (event = {}) => {
    setDeleteDialogState((prevState) => !prevState);
  };

  const handleBulkUpdate = (rowId, action) => {
    setAllocations((prevAllocations) =>
      prevAllocations.map((allocation) => {
        return allocation.id === rowId ? { ...allocation, isSelected: action } : allocation
      })
    );
  };

  const selectAllBtnHandler = (selecteData) => {
    setSelectedCount(selecteData.filter(_ => _.status === 'PENDING' && _.isSelected).length)
    setBulkAllocationModal(!bulkAllocationModal)
  }

  const handleApprove = () => {
    setBulkAllocationModal(false);
    setPendingCommentAction("APPROVED");
    setIsBulkContext(true);
    setReviewCommentModalOpen(true);
  }

  const handleDelete = () => {
    bulkDeleteDialogHandler()
  }

  const handleReject = () => {
    setBulkAllocationModal(false);
    setPendingCommentAction("REJECTED");
    setIsBulkContext(true);
    setReviewCommentModalOpen(true);
  }

  const handleCancel = () => {
    setBulkAllocationModal(false)
  }

  const handleBulkReviewCommentSubmit = (comment) => {
    setReviewComment(comment);
    setReviewCommentModalOpen(false);
    updateBulkTimesheetStatus(pendingCommentAction, comment);
    setPendingCommentAction(null);
  };

  const updateBulkTimesheetStatus = async (status, comment = "") => {
    handleClose();
    const selectedAllocations = allocations.filter(_ => _.status === "PENDING" && _.isSelected);
    const allocationIds = selectedAllocations.map(_ => _.id);
    const userNameMap = {};
    selectedAllocations.forEach(a => {
      userNameMap[a.id] = a.empName;
    });

    const data = {
      allocationIds,
      status,
      comment: comment || reviewComment || "",
      userNameMap
    };
    try {
      await dispatch(bulkUpdateTimesheet(data));
      getAllocations();
      setBulkAllocationModal(false);
      setReviewComment("");
      handleCancel();
    } catch (e) {
      console.error("Failed to bulk update timesheets", e);
    }
  }

  const bulkDeleteDialogHandler = async () => {
    // delete all selected allocations, then refresh once all deletions complete
    const toDelete = allocations.filter(a => a.status === "PENDING" && a.isSelected);
    if (toDelete.length > 0) {
      await dispatch(deleteResources("WORK", { allocationIds: toDelete.map(_ => _.id) }))
    }
    handleClose();
    await getAllocations();
    setBulkAllocationModal(false);
  }

  return (
    <>
      <DeleteDialog
        open={deleteDialogState}
        id={allocationId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogHandler}
        moduleName="Allocation"
        description="Allocation"
      />
      <ReviewCommentModal
        open={reviewCommentModalOpen}
        onClose={() => {
          setReviewCommentModalOpen(false);
          setPendingCommentAction(null);
          setPendingAction(null);
          setReviewComment("");
          setIsBulkContext(false);
        }}
        onSubmit={isBulkContext ? handleBulkReviewCommentSubmit : handleReviewCommentSubmit}
        action={isBulkContext ? pendingCommentAction : pendingAction}
        isBulk={isBulkContext}
        count={selectedCount}
      />
      <BulkAllocationModal
        selectedCount={selectedCount}
        onApprove={handleApprove}
        onDelete={handleDelete}
        onReject={handleReject}
        onRecalculate={() => runRecalculation(allocations.filter((allocation) => allocation.status === "PENDING" && allocation.isSelected).map((allocation) => allocation.id))}
        onCancel={handleCancel}
        open={bulkAllocationModal}
        dialogClose={handleCancel}
        processing={isRecalculating}
      />
      <TimesheetActionConfirmation
        open={actionConfirmationState}
        dialogClose={() => setActionConfirmationState(false)}
        onConfirm={confirmStatusUpdate}
        action={pendingAction}
        timesheetId={allocationId}
      />
      <TimesheetActionConfirmation
        open={recalculateConfirmationOpen}
        dialogClose={() => {
          if (!isRecalculating) {
            setRecalculateConfirmationOpen(false);
            setRecalculateIds([]);
          }
        }}
        onConfirm={confirmRecalculation}
        action="RECALCULATE"
        count={recalculateIds.length}
        isBulk={recalculateIds.length > 1}
        hideCommentField
      />
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {timesheetStatus === "PENDING" && (
          <MenuItem onClick={modify}>Modify</MenuItem>
        )}
        {timesheetStatus === "PENDING" && (
          <MenuItem onClick={() => updateTimesheetStatus("APPROVED")}>
            Accept/Approve
          </MenuItem>
        )}
        {timesheetStatus === "PENDING" && (
          <MenuItem onClick={() => updateTimesheetStatus("REJECTED")}>
            Reject
          </MenuItem>
        )}
        {timesheetStatus === "PENDING" && (
          <MenuItem onClick={() => deleteDialogHandler()}>
            Delete
          </MenuItem>
        )}
        {timesheetStatus === "PENDING" && (
          <MenuItem onClick={() => requestRecalculation([allocationId])} data-cy="action-recalculate">
            Recalculate
          </MenuItem>
        )}
        {timesheetStatus !== "PENDING" && (
          <MenuItem onClick={() => updateTimesheetStatus("PENDING")}>
            Set as Pending
          </MenuItem>
        )}
      </Menu>
      {showAllocateResource && (
        <AllocateResource
          open={showAllocateResource}
          dialogClose={() => setShowAllocateResource(false)}
          allocateResource={allocateResource}
          allocationType={"WORK"}
          allocationId={allocationId}
          applyLeave={applyLeave}
        />
      )}
      {confirmDialogState && (
        <ConfirmAllocateResource
          open={confirmDialogState}
          data={confirmData}
          dialogClose={() => setConfirmDialogState(false)}
          allocateSchedule={allocateScheduleHandler}
          modifyAllocation={(data) => dispatch(modifyAllocation(data))}
          getAllocations={getAllocations}
        />
      )}
      <Grid container sx={{ m: 0, mb: 1 }}>
        <Grid item xs={12} sx={{ p: 0 }} textAlign={"start"}>
          <ArrowCircleLeftOutlinedIcon
            className="arrow-circle-icon"
            onClick={cancelHandler}
          />
          <span className="link" onClick={cancelHandler}>
            Timesheet
          </span>
          <span className="arrow"> {"/"} </span>
          {"View Timesheet"}
        </Grid>
      </Grid>

      {filterParams && (
        <div className="filter-toggle" style={{ marginBottom: '15px', display: 'flex', justifyContent: 'flex-end' }}>
          <Form.Check
            type="switch"
            id="filter-toggle"
            label={useFilters ? "Showing filtered data" : "Showing all data"}
            checked={useFilters}
            onChange={() => setUseFilters(!useFilters)}
          />
        </div>
      )}

      <div className="tableContainer timesheetScrollMobile">
        <div className="timesheetScroll">
          <div className="mobileTimesheet">
            <CustomTable
              columns={columns}
              data={allocations}
              isAction={true}
              handleMenu={(data) => handleMenu(data)}
              selectAllAction={true}
              selectAllBtnHandler={selectAllBtnHandler}
              selectAll={selectAll}
              menu={(data) => (
                <Popover id="popover-basic" data-cy="action-menu-popover">
                  <Popover.Body>
                    <ul className="action-block-list">
                      {data.status === "PENDING" && (
                        <li onClick={modify} data-cy="action-modify">Modify</li>
                      )}
                      {data.status === "PENDING" && (
                        <li onClick={() => updateTimesheetStatus("APPROVED")} data-cy="action-approve">
                          Accept/Approve
                        </li>
                      )}
                      {data.status === "PENDING" && (
                        <li onClick={() => updateTimesheetStatus("REJECTED")} data-cy="action-reject">
                          Reject
                        </li>
                      )}
                      {data.status === "PENDING" && (
                        <li onClick={() => deleteDialogHandler()} data-cy="action-delete">
                          Delete
                        </li>
                      )}
                      {data.status === "PENDING" && (
                        <li onClick={() => requestRecalculation([data.id])} data-cy="action-recalculate">
                          Recalculate
                        </li>
                      )}
                      {data.status !== "PENDING" && (
                        <li onClick={() => updateTimesheetStatus("PENDING")} data-cy="action-pending">
                          Set as Pending
                        </li>
                      )}
                    </ul>
                  </Popover.Body>
                </Popover>
              )}
              fileName="Allocation List"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewTimeSheet;